import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DownloadOutlined, KeyOutlined, PieChartOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Tooltip, theme } from "antd";
import { observer } from "mobx-react";
import moment from "moment";
import { useState } from "react";
import { LicenseAllocationUpgradePromo } from "./inner/licenseAllocationUpgradePromo.js";
import { LicenseKeyModal } from "./inner/licenseKeyModal.js";
import { LicenseKeyRow } from "./inner/licenseKeyRow.js";
import { SubscriptionLicenseAllocationCapacityBar } from "./inner/subscriptionLicenseAllocationCapacityBar.js";
import { trackAnalyticsEvent } from "../../../../../others/tracking.js";
import { useStores } from "../../../../../store/stores.js";
import { __ } from "../../../../../utils/i18n.js";
import { usePlanCapacity } from "../../../hooks/usePlanCapacity.js";
import { EmptyState } from "../../emptyState.js";
// RPM appends " PRO" to distinguish the paid build from the free sibling;
// strip for display — the subscription header already names the product.
// TODO: remove once RPM drops the build-flavour suffix from display names.
const stripProSuffix = (name)=>name.replace(/\s+PRO$/, "");
const SubscriptionDetailBody = observer(({ subscription })=>{
    const { token } = theme.useToken();
    const { accountV2Store } = useStores();
    const [isAddOpen, setIsAddOpen] = useState(false);
    const { licenses } = subscription;
    const installations = subscription.installations ?? [];
    const productVariants = subscription.productVariants ?? [];
    const shouldShowProductColumn = productVariants.length > 1;
    // Stack the chip pair across the *whole* list when at least one key
    // carries a description — otherwise the description would crowd that
    // single row while the others kept the inline layout, and the section
    // would read inconsistently. With no descriptions at all the chips can
    // stay side-by-side for a tighter row.
    const hasAnyDescription = licenses.some((l)=>!!l.data.description);
    const capacity = usePlanCapacity(subscription);
    const [primaryProduct] = installations;
    // Promo banner: surface when at least one website pool is fully allocated
    // *and* the customer has had time to commit to the product. The age
    // threshold shifts depending on the original plan size — fresh-off-the-
    // checkout buyers shouldn't be pushed to upgrade before they've even
    // installed the plugin:
    //   - Multi-site plan (productionTotal > 1) → wait 3 days so the rollout
    //     can finish before we pitch a bigger plan.
    //   - Single-site plan (productionTotal === 1) → wait 30 days so the
    //     upsell narrative ("you need another site") only fires once real
    //     usage has had time to develop.
    // Hidden on plans that can't be upgraded anyway.
    const subscriptionAgeDays = moment().diff(moment(subscription.data.createdAt), "days");
    const minAgeDays = capacity.productionTotal > 1 ? 3 : 30;
    const isMatureSubscription = subscriptionAgeDays >= minAgeDays;
    const exhaustedPools = [
        capacity.isProductionExhausted ? "production" : null,
        capacity.isDevelopmentExhausted ? "development" : null
    ].filter((p)=>p !== null);
    const showUpgradePromo = exhaustedPools.length > 0 && isMatureSubscription && subscription.isActive && !subscription.isOneTime && subscription.canBeModified && // Past-due locks the subscription; the reminder above already owns
    // the customer's attention, so an upgrade pitch would compete with
    // — and at that moment isn't actionable anyway.
    !subscription.isReadOnly;
    return /*#__PURE__*/ _jsxs("div", {
        className: "rc-account-v2-subscription-card__detail",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-license-area",
                children: [
                    primaryProduct && /*#__PURE__*/ _jsxs("div", {
                        className: "rc-account-v2-section-toolbar",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "rc-account-v2-section-toolbar__product",
                                style: {
                                    color: token.colorTextHeading
                                },
                                children: stripProSuffix(primaryProduct.data.productName)
                            }),
                            !!primaryProduct.data.downloadUrl && /*#__PURE__*/ _jsxs("span", {
                                className: "rc-account-v2-section-toolbar__download",
                                children: [
                                    /*#__PURE__*/ _jsx("a", {
                                        href: __("https://devowl.io/knowledge-base/devowl-install-wordpress-plugin/"),
                                        target: "_blank",
                                        rel: "noopener noreferrer",
                                        className: "rc-account-v2-section-toolbar__install-link",
                                        style: {
                                            color: token.colorTextSecondary
                                        },
                                        children: __("Installation guide")
                                    }),
                                    /*#__PURE__*/ _jsx(Tooltip, {
                                        title: subscription.isReadOnly ? __("Update your payment method to download the plugin again.") : "",
                                        children: /*#__PURE__*/ _jsx(Button, {
                                            type: "primary",
                                            icon: /*#__PURE__*/ _jsx(DownloadOutlined, {}),
                                            disabled: subscription.isReadOnly,
                                            onClick: ()=>{
                                                trackAnalyticsEvent("download_product", {
                                                    ga: {
                                                        event_category: "ecommerce",
                                                        event_label: "Download product",
                                                        value: primaryProduct.data.productName
                                                    },
                                                    matomo: {
                                                        category: "ecommerce",
                                                        name: "Download product",
                                                        value: primaryProduct.data.productName
                                                    }
                                                });
                                                window.location.href = primaryProduct.data.downloadUrl;
                                            },
                                            children: __("Download plugin")
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    primaryProduct && /*#__PURE__*/ _jsxs("section", {
                        className: "rc-account-v2-section-card",
                        "aria-label": __("License allocation"),
                        children: [
                            /*#__PURE__*/ _jsxs("header", {
                                className: "rc-account-v2-section-card__head",
                                children: [
                                    /*#__PURE__*/ _jsx(PieChartOutlined, {
                                        className: "rc-account-v2-section-card__icon",
                                        style: {
                                            color: token.colorPrimary
                                        },
                                        "aria-hidden": true
                                    }),
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "rc-account-v2-section-card__heading",
                                        style: {
                                            color: token.colorTextHeading
                                        },
                                        children: __("License allocation")
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "rc-account-v2-section-card__intro",
                                style: {
                                    color: token.colorTextSecondary
                                },
                                children: __("How many of your licenses are currently allocated to your license keys.")
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "rc-account-v2-section-card__bars",
                                children: [
                                    /*#__PURE__*/ _jsx(SubscriptionLicenseAllocationCapacityBar, {
                                        used: capacity.productionUsed,
                                        total: capacity.productionTotal,
                                        label: __("Production websites")
                                    }),
                                    /*#__PURE__*/ _jsx(SubscriptionLicenseAllocationCapacityBar, {
                                        used: capacity.developmentUsed,
                                        total: capacity.developmentTotal,
                                        label: __("Development websites")
                                    })
                                ]
                            }),
                            showUpgradePromo && /*#__PURE__*/ _jsx(LicenseAllocationUpgradePromo, {
                                productName: subscription.data.productPlan.product.name,
                                exhaustedPools: exhaustedPools,
                                onUpgradeClick: ()=>accountV2Store.openChangePlanFor(subscription, {
                                        autoSelectFirstUpgrade: true
                                    })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("section", {
                        className: "rc-account-v2-section-card",
                        "aria-label": __("License keys"),
                        children: [
                            /*#__PURE__*/ _jsxs("header", {
                                className: "rc-account-v2-section-card__head",
                                children: [
                                    /*#__PURE__*/ _jsx(KeyOutlined, {
                                        className: "rc-account-v2-section-card__icon",
                                        style: {
                                            color: token.colorPrimary
                                        },
                                        "aria-hidden": true
                                    }),
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "rc-account-v2-section-card__heading",
                                        style: {
                                            color: token.colorTextHeading
                                        },
                                        children: __("License keys")
                                    }),
                                    /*#__PURE__*/ _jsx(Tooltip, {
                                        title: !subscription.canBeModified ? __("Not available for codecanyon.net purchases.") : subscription.isReadOnly ? __("Update your payment method to manage license keys again.") : "",
                                        children: /*#__PURE__*/ _jsx(Button, {
                                            type: "primary",
                                            icon: /*#__PURE__*/ _jsx(PlusOutlined, {}),
                                            disabled: !subscription.canBeModified || subscription.isReadOnly,
                                            onClick: ()=>setIsAddOpen(true),
                                            className: "rc-account-v2-section-card__action",
                                            children: __("Create license key")
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "rc-account-v2-section-card__intro rc-account-v2-section-card__intro--no-divider",
                                style: {
                                    color: token.colorTextSecondary
                                },
                                children: __("Use a license key to activate the plugin on a website. Split your licenses across multiple keys if you want to manage them separately - for example, one key per client.")
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "rc-account-v2-section-card__body",
                                children: licenses.length === 0 ? /*#__PURE__*/ _jsx(EmptyState, {
                                    heading: __("No license keys yet")
                                }) : licenses.map((license)=>/*#__PURE__*/ _jsx(LicenseKeyRow, {
                                        license: license,
                                        subscription: subscription,
                                        shouldShowProductColumn: shouldShowProductColumn,
                                        shouldStackAllocation: hasAnyDescription
                                    }, license.data.id))
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(LicenseKeyModal, {
                subscription: subscription,
                open: isAddOpen,
                onClose: ()=>setIsAddOpen(false)
            })
        ]
    });
});
export { SubscriptionDetailBody };
